import { trigger } from '@angular/animations';

import { ChangeDetectionStrategy, Component, OnDestroy, computed, effect, inject, model, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { GrowthBook } from '@growthbook/growthbook';
import {
  AssetStatus,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
  GalleryPresetDto,
  GalleryPublicAccessLevel,
  GalleryStatus,
  TenantDto,
  UpdateGalleryBodyDto,
  WatermarkDto,
} from '@nusign/picflow-backend-types';
import { Subject, firstValueFrom, from, take, takeUntil } from 'rxjs';
import { ActionBoxButtonComponent } from '../../../account-gallery-shared/components/action-box/action-box-button/action-box-button.component';
import { ActionBoxContentComponent } from '../../../account-gallery-shared/components/action-box/action-box-content/action-box-content.component';
import { ActionBoxIconComponent } from '../../../account-gallery-shared/components/action-box/action-box-icon/action-box-icon.component';
import { ActionBoxSwitchComponent } from '../../../account-gallery-shared/components/action-box/action-box-switch/action-box-switch.component';
import { ActionBoxComponent } from '../../../account-gallery-shared/components/action-box/action-box.component';
import { NewFeatureDotComponent } from '../../../account-gallery-shared/components/new-feature-dot/new-feature-dot.component';
import { NewFeatureDirective } from '../../../account-gallery-shared/directives/new-feature.directive';
import { ShareGalleryDialogComponent } from '../../../account/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { ShareSubGalleryDialogComponent } from '../../../account/dialogs/share-sub-gallery-dialog/share-sub-gallery-dialog.component';
import { maxHeightIf } from '../../../animations/max-height.if.animation';
import { billingScopes } from '../../../billing-consts';
import { IsDarkColorPipe } from '../../../color-picker/is-dark-color.pipe';
import { ColorSelectorComponent } from '../../../color-selector/color-selector.component';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { StandardGalleryPreset } from '../../../configs/standard-presets';
import { downloadSizes, maxWatermarkApplyTime } from '../../../consts';
import { GROWTHBOOK } from '../../../growthbook';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { capitalize } from '../../../lib/capitalize';
import { convertGalleryDownloadSize } from '../../../lib/convert-gallery-download-size';
import { copy } from '../../../lib/copy';
import { debounce } from '../../../lib/debounce';
import { getFormControl } from '../../../lib/get-form-control';
import { loadFont } from '../../../lib/load-font';
import { getPresetChanges } from '../../../lib/new-gallery';
import { sleep } from '../../../lib/sleep';
import { uppercase } from '../../../lib/uppercase';
import { AccessLevelService } from '../../../services/access-level.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { GalleryMetaImageUploadService } from '../../../services/gallery-meta-image-upload.service';
import { GalleryPresetService } from '../../../services/gallery-preset.service';
import { GalleryService } from '../../../services/gallery.service';
import { NewFeatureDotService } from '../../../services/new-feature-dot.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { WatermarkService } from '../../../services/watermark.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { RadioButtonComponent } from '../../../shared-extra/components/radio-button/radio-button.component';
import { StickyFooterComponent } from '../../../shared-extra/components/sticky-footer/sticky-footer.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { InputPreDirective } from '../../../shared-extra/directives/input-pre.directive';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';
import { FontSelectionMenuComponent } from '../../components/font-selection-menu/font-selection-menu.component';
import { GallerySettingsPresetsContextComponent } from '../../components/gallery-settings-presets-context/gallery-settings-presets-context.component';
import { findPreset } from '../../lib/find-preset';
import { SaveGalleryPresetDialogComponent } from '../save-gallery-preset-dialog/save-gallery-preset-dialog.component';
import { GallerySettingsMenuItems } from './gallery-settings-menu';

@Component({
  selector: 'app-gallery-settings-dialog',
  templateUrl: './gallery-settings-dialog.component.html',
  styleUrls: ['./gallery-settings-dialog.component.scss'],
  animations: [trigger('approvalLimitForm', maxHeightIf(72))],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ResizeDirective,
    ButtonDirective,
    SvgComponent,
    NewFeatureDirective,
    NewFeatureDotComponent,
    ContextMenuComponent,
    TooltipDirective,
    GallerySettingsPresetsContextComponent,
    ActionBoxComponent,
    ActionBoxIconComponent,
    ActionBoxContentComponent,
    ActionBoxSwitchComponent,
    RouterLink,
    ButtonMenuItemComponent,
    LoadingIndicatorComponent,
    TransitionPreventorDirective,
    FormsModule,
    UpgradeBadgeComponent,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPreDirective,
    InputPostDirective,
    ActionBoxButtonComponent,
    RadioButtonComponent,
    StickyFooterComponent,
    CapitalizePipe,
    BillingScopePipe,
    GalleryTeaserUrlPipe,
    ColorSelectorComponent,
    IsDarkColorPipe,
    FontSelectionMenuComponent,
    TransitionPreventorDirective,
    SwitchComponent,
  ],
})
export class GallerySettingsDialogComponent implements OnGetData, OnDestroy {
  public state = inject(StateService);
  private dialog = inject(DialogService);
  private galleryService = inject(GalleryService);
  private router = inject(Router);
  private analytics = inject(AnalyticsService);
  private toasts = inject(ToastService);
  private galleryPresetService = inject(GalleryPresetService);
  private permissionService = inject(PermissionService);
  private newFeature = inject(NewFeatureDotService);
  private accessLevelService = inject(AccessLevelService);
  private watermarkService = inject(WatermarkService);
  private assetService = inject(AssetService);
  private sectionService = inject(SectionService);
  private galleryMetaImageUploadService = inject(GalleryMetaImageUploadService);
  public growthBook = inject<GrowthBook>(GROWTHBOOK);
  dialogName = 'gallery-settings';
  mode = signal('access');
  gallery = signal<GalleryDto>(undefined);
  tenant = computed(() => this.gallery()?.tenant as TenantDto);
  close: (...args) => void;
  showApprovalLimit = model<boolean>(false);
  approvalLimitForm = new FormGroup({ limit: getFormControl('approval-limit'), softLimit: new FormControl(false) });
  customColorLabels = model<boolean>(false);
  customLabelsForm = new FormGroup({});
  standardPresets = signal<StandardGalleryPreset[]>(undefined);
  customPresets = signal<GalleryPresetDto[]>(undefined);
  currentPreset = computed(() => {
    const gallery = this.gallery();
    const standardPresets = this.standardPresets();
    const customPresets = this.customPresets();
    if (!gallery || !standardPresets || !customPresets) return;
    return findPreset(gallery, standardPresets, customPresets);
  });
  watermarks = toSignal(this.watermarkService.watermarks$);
  isWatermarkApplying = signal<boolean>(false);
  destroyed = false;
  ambientMode = model<boolean>(false);
  watermarkOptionCheckedOverride = model<boolean>(undefined);
  approveOnly = model<boolean>(false);
  annotationsEnabled = model<boolean>(false);
  menuItems = GallerySettingsMenuItems;
  enabledDownloadSizes = computed(() =>
    this.gallery() ? convertGalleryDownloadSize(this.gallery().download_media_size) : undefined
  );

  downloadSizeDisabled = computed(() => {
    const gallery = this.gallery();
    return !gallery.download_single_asset && !gallery.download_full_gallery;
  });

  downloadSizeString = computed(() => {
    const enabledDownloadSizes = this.enabledDownloadSizes();
    if (enabledDownloadSizes === undefined) return 'Loading...';
    const enabledCount = Object.values(enabledDownloadSizes)
      .map(a => (a ? 1 : 0))
      .reduce((t, v) => t + v, 0);
    const maxCount = this.downloadSizes.length;
    if (enabledCount >= maxCount) return 'All Sizes';
    if (enabledCount > 1) return `${enabledCount} Sizes`;
    const enabled = Object.keys(enabledDownloadSizes).find(size => enabledDownloadSizes[size]);
    return capitalize(enabled);
  });
  selectedWatermark = computed(() => {
    const gallery = this.gallery();
    const watermarks = this.watermarks();
    if (!gallery || !watermarks) return;
    return watermarks.find(watermark => watermark.id === gallery.watermark);
  });
  tempSelectedWatermark = signal<WatermarkDto>(undefined);
  downloadSizes = downloadSizes.filter(s => s.name !== 'reserved');
  galleryGuest = signal<boolean>(undefined);
  publicAccessLevel = signal<GalleryPublicAccessLevel>(undefined);
  destroyed$ = new Subject<void>();

  dialogOptions(): DialogOptions {
    return { size: 940, spacingX: 0, spacingY: 0 };
  }

  constructor() {
    effect(
      () => {
        const gallery = this.gallery();
        if (!gallery) return;

        if (this.permissionService.canDisplayElement(billingScopes.asset.customLabels)) {
          const hasCustomColorLabel = !!gallery.color_labels.find(l => !l.active || l.name !== uppercase(l.color));
          if (hasCustomColorLabel) this.customColorLabels.set(true);
        }

        const enabledStatuses: AssetStatus[] = gallery.enabled_asset_statuses || [];
        if (enabledStatuses.length !== 2) this.approveOnly.set(false);
        else this.approveOnly.set(enabledStatuses.includes('approved'));
        this.annotationsEnabled.set(gallery.enabled_feedback_options?.includes('annotation'));
        this.ambientMode.set(gallery.asset_ambient_mode);
        this.accessLevelService
          .isGalleryGuest(gallery)
          .pipe(take(1))
          .subscribe(isGalleryGuest => {
            this.galleryGuest.set(isGalleryGuest);
          });

        from(this.accessLevelService.getAccessLevels(gallery))
          .pipe(take(1))
          .subscribe(([, publicAccessLevel]) => {
            this.publicAccessLevel.set(publicAccessLevel);
          });
        gallery.color_labels.forEach(label => {
          const currentControl = this.customLabelsForm.get(label.color);
          if (currentControl && currentControl.value === label.name) return;
          this.customLabelsForm.removeControl(label.color);
          this.customLabelsForm.addControl(label.color, getFormControl('customLabel', label.name));
        });
        if (gallery.approval_limits) this.showApprovalLimit.set(true);
        if (this.showApprovalLimit()) {
          const hardLimit = gallery.approval_limits >= 0;

          if (this.approvalLimitForm.value.limit === undefined) {
            this.approvalLimitForm.setValue({
              limit: hardLimit ? gallery.approval_limits : -gallery.approval_limits,
              softLimit: !hardLimit,
            });
          }
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const isGalleryGuest = this.galleryGuest();
        if (isGalleryGuest !== false) return;
        from(this.galleryPresetService.getStandardGalleryPresets())
          .pipe(takeUntil(this.destroyed$))
          .subscribe(presets => {
            this.standardPresets.set(presets);
          });

        from(this.galleryPresetService.getCustomGalleryPresets())
          .pipe(takeUntil(this.destroyed$))
          .subscribe(presets => {
            this.customPresets.set(presets);
          });
      },
      { allowSignalWrites: true }
    );
  }

  async onGetData(data: { gallery: GalleryDto; defaultMode: string }, close: (...args) => void) {
    this.approvalLimitForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (!value.limit) return;
      this.updateApprovalLimitDebounced();
    });

    if (!data.gallery) {
      console.warn('gallery settings started without gallery');
      return close();
    }

    this.galleryService.onGalleryDeleted$.pipe(takeUntil(this.destroyed$)).subscribe(async gallery => {
      if (!gallery || !this.gallery() || gallery.id !== this.gallery().id) return;
      this.close();
    });

    this.close = close;
    this.gallery.set(copy(data.gallery));
    this.galleryService
      .getGallery(data.gallery.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(gallery => this.gallery.set(gallery));

    if (data.defaultMode) {
      this.mode.set(data.defaultMode);
      this.newFeature.markFeatureAsSeen(`gallery-settings-${this.mode()}`);
    }

    this.isWatermarkApplying.set(await this.checkIsApplyingWatermark());
    if (this.isWatermarkApplying()) {
      await this.waitTillNotApplying();
      this.isWatermarkApplying.set(false);
    }
  }

  async openParentGallerySettingsDialog() {
    const parentGallery = await firstValueFrom(this.galleryService.getGallery(this.gallery['parent_gallery_id']));
    this.dialog.open(GallerySettingsDialogComponent, { gallery: parentGallery });
  }

  updateApprovalLimit() {
    if (this.showApprovalLimit()) {
      if (!this.gallery().approval_limits && !this.approvalLimitForm.value.limit) {
        this.approvalLimitForm.setValue(
          {
            limit: '',
            softLimit: false,
          },
          { emitEvent: false }
        );
      } else {
        if (!this.approvalLimitForm.valid) return;
        let limit = this.approvalLimitForm.value.limit;

        if (!limit) limit = 0;

        if (this.approvalLimitForm.value.softLimit && limit > 0) {
          // hard limit and soft limit are determined by using positive and negative numbers respectively
          limit = -limit;
        }
        if (this.gallery().approval_limits !== limit) {
          this.updateGallery({ approval_limits: limit });
        }
      }
    } else if (this.gallery().approval_limits) {
      this.updateGallery({ approval_limits: null });
    }
  }

  updateApprovalLimitDebounced = debounce(() => this.updateApprovalLimit(), 500);

  async saveGalleryStatus(value: boolean) {
    const allGalleryStatuses: GalleryStatus[] = ['approved', 'review', 'no_status', 'progress']; // 'revisions'
    await this.updateGallery({ enabled_gallery_statuses: value ? allGalleryStatuses : ['no_status'] });
  }

  async saveFeedbackOptions(key: GalleryFeedbackOptions, value: boolean) {
    const currentlyEnabled = this.gallery().enabled_feedback_options.includes(key);

    let enabledFeedbackOptions = this.gallery().enabled_feedback_options;

    if (key === 'comment') {
      // if we change the value for comments, we also need to change the value for annotations
      if (value) {
        enabledFeedbackOptions.push('annotation');
      } else {
        enabledFeedbackOptions = enabledFeedbackOptions.filter(o => o !== 'annotation');
      }
    }

    if (currentlyEnabled && !value) {
      // filtering out the key
      enabledFeedbackOptions = enabledFeedbackOptions.filter(o => o !== key);
    }

    if (!currentlyEnabled && value) {
      // adding the key
      enabledFeedbackOptions.push(key);
    }
    this.updateGallery({ enabled_feedback_options: enabledFeedbackOptions });
  }

  async updateBackgroundColor(color: string) {
    this.saveBackgroundColor(color);
    this.galleryService.galleryUpdateTimeMap.set(this.gallery().id, Date.now());
  }

  saveBackgroundColor = debounce((backgroundColor: string) => {
    this.galleryService.updateGallery(this.gallery().id, {
      layout_background_color: backgroundColor || null,
    });
  }, 500);

  async updateAssetBackgroundColor(color: string) {
    this.saveAssetBackgroundcolor(color);
    this.galleryService.galleryUpdateTimeMap.set(this.gallery().id, Date.now());
  }

  private saveAssetBackgroundcolor = debounce((color: string) => {
    this.galleryService.updateGallery(this.gallery().id, {
      asset_background_color: color || null,
    });
  }, 500);

  updateTitleFontFamily(font: string) {
    loadFont(font);
    this.updateGallery({
      default_title_typography: { ...this.gallery().default_title_typography, font_family: font },
    });
  }

  updateParagraphFontFamily(font: string) {
    loadFont(font);
    this.updateGallery({
      default_paragraph_typography: { ...this.gallery().default_paragraph_typography, font_family: font },
    });
  }

  updateTitleFontColor(color: string) {
    this.saveTitleFontColor(color);
    this.galleryService.galleryUpdateTimeMap.set(this.gallery().id, Date.now());
  }

  updateParagraphFontColor(color: string) {
    this.saveParagraphFontColor(color);
    this.galleryService.galleryUpdateTimeMap.set(this.gallery().id, Date.now());
  }

  saveTitleFontColor = debounce((color: string) => {
    this.updateGallery({
      default_title_typography: { ...this.gallery().default_title_typography, font_color: color },
    });
  }, 500);

  saveParagraphFontColor = debounce((color: string) => {
    this.updateGallery({
      default_paragraph_typography: { ...this.gallery().default_paragraph_typography, font_color: color },
    });
  }, 500);

  async applyWatermark(watermark: WatermarkDto) {
    this.watermarkOptionCheckedOverride.set(undefined);
    if (watermark) this.isWatermarkApplying.set(true);
    await this.updateGallery({ watermark: watermark?.id || null });
    if (watermark) {
      this.close();
      this.toasts.add('Applying watermarks', 'success');
      await sleep(1000);
      this.isWatermarkApplying.set(false);
    }
  }

  setWatermarking(value: boolean) {
    if (value === !!this.gallery().watermark) {
      this.watermarkOptionCheckedOverride.set(undefined);
      return;
    }

    this.watermarkOptionCheckedOverride.set(value);
    if (!value && this.gallery().watermark) {
      this.applyWatermark(undefined);
    }
  }

  private getEnabledDownloadSizesCount() {
    return Object.values(this.enabledDownloadSizes())
      .map(a => (a ? 1 : 0))
      .reduce((t, v) => t + v, 0);
  }

  toggleDownloadSize(size: string) {
    const enabledDownloadSizes = this.enabledDownloadSizes();
    if (enabledDownloadSizes[size] && this.getEnabledDownloadSizesCount() === 1) return;

    enabledDownloadSizes[size] = !enabledDownloadSizes[size];
    const downloadSizesString = downloadSizes.map(s => (enabledDownloadSizes[s.name] ? '1' : '0')).join('');

    let downloadMediaSize = parseInt(downloadSizesString, 2);
    const maxSize = Math.pow(2, downloadSizes.filter(d => d.name !== 'reserved').length) - 1;
    if (downloadMediaSize === maxSize) downloadMediaSize = 999999;
    this.updateGallery({ download_media_size: downloadMediaSize });
  }

  saveCustomLabels = debounce(async () => {
    const colorLabels = this.gallery().color_labels.map(label => copy(label));
    Object.keys(this.customLabelsForm.value).forEach(key => {
      const label = colorLabels.find(l => l.color === key);
      if (!label) return;
      label.name = this.customLabelsForm.value[key].slice(0, 16) || capitalize(label.color);
    });
    await this.updateGallery({ color_labels: colorLabels });
  }, 1000);

  async onToggleCustomLabels() {
    if (!this.customColorLabels()) {
      const colorLabels: GalleryColorLabel[] = [
        { color: 'red', name: 'Red', active: true },
        { color: 'orange', name: 'Orange', active: true },
        { color: 'blue', name: 'Blue', active: true },
        { color: 'purple', name: 'Purple', active: true },
        { color: 'green', name: 'Green', active: true },
      ];
      await this.updateGallery({ color_labels: colorLabels });
    }
  }

  async onToggleApproveOnly() {
    const enabledAssetStatuses: AssetStatus[] = this.approveOnly()
      ? ['approved', 'no_status']
      : ['approved', 'in_progress', 'needs_review', 'rejected', 'no_status'];
    await this.updateGallery({ enabled_asset_statuses: enabledAssetStatuses });
  }

  async uploadTeaserEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadTeaser(files[0]);
  }

  async uploadTeaser(file: File) {
    if (!file) return;
    this.galleryMetaImageUploadService.upload(file, this.gallery(), 'teaser');
  }

  async removeTeaser() {
    await this.updateGallery({
      teaser: {
        ...this.gallery().teaser,
        uuid: null,
      },
    });
  }

  openShareDialog() {
    if (this.gallery().section) {
      this.dialog.open(ShareSubGalleryDialogComponent, { gallery: this.gallery() });
    } else {
      this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery() });
    }
  }

  applyPreset(preset: GalleryPresetDto | StandardGalleryPreset) {
    const params = getPresetChanges(preset);
    this.showApprovalLimit.set(!!this.gallery().approval_limits);
    this.updateGallery(params);
  }

  public async updateGallery(params: UpdateGalleryBodyDto) {
    const resp = await this.galleryService.updateGallery(this.gallery().id, params);
    return resp;
  }

  public async refreshWatermark() {
    if (this.isWatermarkApplying()) return;
    this.galleryService.refreshWatermark(this.gallery().id);
    this.toasts.add('Updating watermarks', 'success');
    this.isWatermarkApplying.set(true);
    await sleep(1000 * 10); // waiting 10 seconds because we don't know when the watermark has been updated
    this.isWatermarkApplying.set(false);
  }

  createPreset() {
    if (!this.permissionService.canDisplayElement(billingScopes.gallery.customPresets)) {
      return this.router.navigate(['/a', 'pricing']);
    }
    this.dialog.open(SaveGalleryPresetDialogComponent, { gallery: this.gallery(), settingsMode: this.mode() });
  }

  async checkIsApplyingWatermark() {
    if (this.destroyed) return;
    if (!this.gallery().watermark) return;
    if (Date.now() - this.gallery().updated_at * 1000 > maxWatermarkApplyTime) {
      // if the gallery has not been updated in the last 2 minutes, we assume that the watermark has been applied
      // This is a workaround because sometimes the watermark fails for an asset
      return false;
    }
    const assets = await this.assetService.getAssetsForGallery(this.gallery().id);
    const isApplying = assets.some(asset => {
      if (asset.type !== 'image') return false;
      if (!asset.watermark_enabled) return false;
      return !asset.version_data.watermark_uuid;
    });
    return isApplying;
  }

  private async waitTillNotApplying() {
    await sleep(1000 * 2);
    const resp = await this.checkIsApplyingWatermark();
    if (!resp) return;
    return this.waitTillNotApplying();
  }

  async onRemoveOverridesTitle() {
    const cover_title = this.gallery().cover.title_typography;
    if (cover_title?.font_family) {
      this.galleryService.updateGallery(this.gallery().id, {
        cover: {
          ...this.gallery().cover,
          title_typography: { ...this.gallery().cover.title_typography, font_family: null },
        },
      });
    }
    const sections = await firstValueFrom(this.sectionService.getSections(this.gallery().id));
    await Promise.all([
      ...sections.map(async section => {
        if (section.object !== 'text') return;
        if (!section.text_title_typography?.font_family) return;
        section.text_title_typography.font_family = null;
        await firstValueFrom(
          this.sectionService.updateSection(section, { text_title_typography: section.text_title_typography })
        );
      }),
    ]);
  }

  async onRemoveOverridesParagraph() {
    const cover_paragraph = this.gallery().cover.paragraph_typography;
    if (cover_paragraph?.font_family) {
      this.galleryService.updateGallery(this.gallery().id, {
        cover: {
          ...this.gallery().cover,
          paragraph_typography: { ...this.gallery().cover.paragraph_typography, font_family: null },
        },
      });
    }
    const sections = await firstValueFrom(this.sectionService.getSections(this.gallery().id));
    await Promise.all([
      ...sections.map(async section => {
        if (section.object !== 'text') return;
        if (!section.text_paragraph_typography?.font_family) return;
        section.text_paragraph_typography.font_family = null;
        await firstValueFrom(
          this.sectionService.updateSection(section, {
            text_paragraph_typography: section.text_paragraph_typography,
          })
        );
      }),
    ]);
  }

  reportCheck(name: string, value: boolean) {
    this.analytics.track('toggle-switch', false, { name, location: 'gallery-settings-dialog', value });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
